import { Fragment, type ReactElement } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import { styled } from "../../../../lib/styled";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { SCORECARD, type ScorecardKey } from "@shared/scorecard";
import { SectionLabel } from "../../../primitives/data-display/SectionLabel";
import { Note } from "../../../primitives/data-display/Note";
import { gaps } from "../../../primitives/spacing";
import type { AnnotationMap, ExtractedScorecardWithEvidence } from "../../../../lib/corpusApi";
import {
  effectiveLabels,
  fieldStatus,
  type FieldStatus,
} from "../../../../lib/scorecardFieldStatus";
import { fieldLabel } from "../../../../lib/scorecardFieldUtils";

interface Props {
  scorecard: ExtractedScorecardWithEvidence | null;
  annotation: AnnotationMap | null;
  /** If provided, the matching row is labelled "Current". Omit for read-only (non-annotation) views. */
  activeField?: ScorecardKey;
}

const STATUS_TOOLTIP: Record<FieldStatus, string> = {
  human_reviewed: "Human-reviewed",
  auto_validated: "Auto-validated — extracted above this tier's confidence threshold",
  needs_review: "Needs review — not yet confident or reviewed enough to trust",
  empty: "Marked empty by a human — no value expected here",
};

// Icons carry no colour of their own (shape + tooltip do the work); the row background is what
// signals status at a glance, mirroring the tier badges' colour convention elsewhere in Kodakku.
const StatusCheckCircle = styled(CheckCircleOutlineIcon)({ width: 20, height: 20 });
const StatusVerified = styled(VerifiedOutlinedIcon)({ width: 20, height: 20 });
const StatusError = styled(ErrorOutlineIcon)({ width: 20, height: 20 });
const StatusRemove = styled(RemoveCircleOutlineIcon)({ width: 20, height: 20 });

const STATUS_ICON: Record<FieldStatus, ReactElement> = {
  human_reviewed: <StatusCheckCircle />,
  auto_validated: <StatusVerified />,
  needs_review: <StatusError />,
  empty: <StatusRemove />,
};

const STATUS_BG_COLOR: Record<FieldStatus, "success" | "warning" | null> = {
  human_reviewed: "success",
  auto_validated: "success",
  needs_review: "warning",
  empty: null,
};

const ALL_FIELDS = Object.keys(SCORECARD) as ScorecardKey[];

const SecondaryIconSlot = styled(Stack)(({ theme }) => ({ color: theme.palette.text.secondary }));

const CurrentFieldRow = styled(Stack)({ alignItems: "center" });

const StatusListItem = styled(ListItem)<{ $color: "success" | "warning" | null }>(
  ({ theme, $color }) => ({
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    ...($color && { backgroundColor: alpha(theme.palette[$color].main, 0.1) }),
  })
);

/**
 * Read-only rollup of every scorecard field's current effective value and where it came from
 * (auto-validated, human-reviewed, or still open) — rendered below the active field's controls
 * so an annotator can see the document's full picture without leaving this view. Matters because
 * some fields act as hints for judging others (e.g. study_type) even when they aren't the field
 * currently under review, and an auto-validated field is otherwise invisible here (it never
 * surfaces in the backlog stream at all).
 */
export function AnnotatedFieldsSummary({ scorecard, annotation, activeField }: Props) {
  return (
    <Stack gap={gaps.tight}>
      <SectionLabel noGutter>All fields on this document</SectionLabel>
      <Note>Auto-validated and previously-reviewed fields are shown for context.</Note>
      <List disablePadding>
        {ALL_FIELDS.map((key, i) => {
          const labels = effectiveLabels(key, scorecard, annotation);
          const status = fieldStatus(key, scorecard, annotation);
          const bgColor = STATUS_BG_COLOR[status];
          return (
            <Fragment key={key}>
              {i > 0 && <Divider component="li" />}
              <StatusListItem
                $color={bgColor}
                secondaryAction={
                  <Tooltip title={STATUS_TOOLTIP[status]}>
                    <SecondaryIconSlot>{STATUS_ICON[status]}</SecondaryIconSlot>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={labels.length > 0 ? labels.join(", ") : "—"}
                  secondary={
                    key === activeField ? (
                      <CurrentFieldRow direction="row" gap={gaps.tight}>
                        {fieldLabel(key)}
                        <Chip label="Current" size="small" color="primary" />
                      </CurrentFieldRow>
                    ) : (
                      fieldLabel(key)
                    )
                  }
                  slotProps={{ secondary: { component: "div" } }}
                />
              </StatusListItem>
            </Fragment>
          );
        })}
      </List>
    </Stack>
  );
}
